import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { isSameDay } from 'date-fns';
import { useApolloClient } from '@apollo/client';
import { useQueryClient } from '@zg-rentals/react-query5';
import { Anchor, Banner, Box, List, Paragraph, Text, TextButton } from '@zillow/constellation-10';
import {
  MESSAGE_LOAD_ERROR_TEXT,
  MESSAGE_STATUS,
  MESSAGE_TYPES,
  type MessageStatusVariant,
  NewMessageNotificationBanner,
} from '@zg-rentals/satellite-components';
import { useSatelliteWebRenterConversationHandler_updateStatus } from '@zg-rentals/ts-satellite-client-react-query';
import { SatelliteWebRenterConversationHandler_getLatestConversations_Path } from '@zg-rentals/ts-satellite-client';
import type { MessageList_Rh_GetConversationQuery } from '../../types/GraphqlTypes';
import {
  MessageList_Rh_GetConversationDocument,
  useMessageList_Rh_GetConversationQuery,
} from '../../types/GraphqlTypes';
import { mapFolderToApiType } from '../../utils/folderUtils';
import { AppContext } from '../../contexts/AppContextProvider';
import useGetFeatureFlagsAndSettings from '../../hooks/useGetFeatureFlagsAndSettings';
import useSmartPolling from '../../hooks/useSmartPolling';
import getMessageTimestamp from '../../utils/getMessageTimestamp';
import arrayUtils from '../../utils/arrayUtils';
import stringUtils from '../../utils/stringUtils';
import { MessageTypes } from '../MessageItem';
import MessageItem from '../MessageItem';
import StringConstants from '../../constants/StringConstants';
import AddMessage from '../AddMessage';
import Disclaimer from '../Disclaimer';
import { MessageListContentsSkeleton } from './skeleton';
import viewUtils from '../../utils/viewUtils';
import { InboxPageContext } from '../../contexts/InboxPageContextProvider';
import { logError } from '@zg-rentals/log-error';
import NoConversationsContent from '../NoConversationsContent';
import { useRouter } from 'next/router';
import TypingIndicator from '../MessageItem/components/TypingIndicator';
import { useWebSocketContext } from '../../contexts/WebSocketContextProvider';
import { WEBSOCKET_DEBUG } from '../../hooks/useWebsocketFeature';
import type { WebSocketMessage } from '../../hooks/useWebSocketConnection';
import { bootstrap } from '../../bootstrap';
import { conversationIntroTextStyles, messageListThreadStyles, messageListWrapperStyles } from './styles';

const DEFAULT_AI_POLL_INTERVAL = 15;
const TYPING_INDICATOR_TIMEOUT_MS = 90000; // 90 seconds

const logger = bootstrap.logger.child({ name: 'MessageList' });

export type DecoratedMessage = ArrayElement<
  MessageList_Rh_GetConversationQuery['RH_getConversationById']['conversation']
> & {
  sendStatus?: MessageStatusVariant;
  temporaryMessageKey?: string;
};

type MessageListProps = {
  shouldAllowSendingAttachmentsOnly: boolean;
  shouldOpenAttachmentsUploader: boolean;
  shouldFetchOlderConversations: boolean;
};

export default function MessageList({
  shouldAllowSendingAttachmentsOnly,
  shouldOpenAttachmentsUploader,
  shouldFetchOlderConversations,
}: MessageListProps) {
  const apolloClient = useApolloClient();
  const queryClient = useQueryClient();
  const { brand, isImpersonator, paths } = useContext(AppContext);
  const { conversationId, folder } = useContext(InboxPageContext);
  const { settings } = useGetFeatureFlagsAndSettings();
  const { aiConversationPollIntervalInSeconds } = settings;
  const aiConversationPollingInterval = (aiConversationPollIntervalInSeconds || DEFAULT_AI_POLL_INTERVAL) * 1000;
  const router = useRouter();

  const {
    query: { utm_source },
  } = router;
  let resizeObserver: ResizeObserver | null = null;

  // Refs
  const addMessageContainerRef = useRef<HTMLDivElement>(null);
  const messageListContainerRef = useRef<HTMLDivElement>(null);
  const messageListEndRef = useRef<HTMLDivElement | null>(null);
  const isMessagesEndInViewport = viewUtils.isElementInViewport(messageListEndRef.current);

  // State
  const [temporaryMessages, setTemporaryMessages] = useState<Array<DecoratedMessage>>([]);
  const [shouldShowLoadingSkeleton, setShouldShowLoadingSkeleton] = useState(true);
  const [shouldShowNewMessageNotification, setShouldShowNewMessageNotification] = useState(false);
  const [isLoadingMoreMessages, setIsLoadingMoreMessages] = useState(false);
  const [previousPaginatedMessages, setPreviousPaginatedMessages] = useState<Array<DecoratedMessage>>([]);
  const [previousPaginatedMessageCount, setPreviousPaginatedMessageCount] = useState<number | null>(null);
  const [shouldShowTypingIndicator, setShouldShowTypingIndicator] = useState(false);

  const { mutateAsync: updateConversationStatusMutation } = useSatelliteWebRenterConversationHandler_updateStatus({
    meta: {
      context: {
        api: 'updateStatus',
        details: 'MessageList - Error marking conversation as read',
      },
    },
  });

  const {
    data: messageListData,
    loading,
    fetchMore,
    error,
    startPolling,
    stopPolling,
    networkStatus,
    refetch,
  } = useMessageList_Rh_GetConversationQuery({
    variables: {
      conversationId: conversationId || '',
    },
    fetchPolicy: 'cache-and-network',
    notifyOnNetworkStatusChange: true,
    ssr: false,
    onCompleted: () => {
      setIsLoadingMoreMessages(false);
    },
    onError: (err) => {
      logError({
        error: err,
        context: {
          info: 'Error fetching MessageList messages',
          networkStatus,
          isNetworkError: !!err.networkError,
          utmSource: utm_source,
        },
      });
    },
    skip: !conversationId,
  });

  const { RH_getConversationById } = messageListData || {};
  const messages = useMemo(
    () =>
      arrayUtils.removeDuplicates(
        [...previousPaginatedMessages, ...(RH_getConversationById?.conversation || []), ...temporaryMessages],
        'messageId',
      ) as Array<DecoratedMessage>,
    [RH_getConversationById?.conversation, temporaryMessages, previousPaginatedMessages],
  );
  const {
    acceptsReplies,
    acceptsRepliesExplanation,
    disclaimers,
    hasUnreadMessage,
    isConversationBlockedByZillow,
    pagination,
  } = RH_getConversationById || {};
  const pageConversationCount = Number(pagination?.pageConversationCount);
  const signInPath = paths.signInPage(brand, `${paths.conversationsPage}/${conversationId}`);
  const aiDisclaimer = disclaimers?.ai;
  const shouldShowChatDisabledMessage = !acceptsReplies && !isConversationBlockedByZillow;
  const shouldShowStartConversationText =
    (previousPaginatedMessageCount !== null && previousPaginatedMessageCount < 10) || pageConversationCount < 10;
  const { code: errorType } = error?.graphQLErrors[0]?.extensions || {};
  const shouldShowReloginNotification = errorType === StringConstants.GRAPHQL_ERROR_TYPES.UNAUTHENTICATED;
  let prevMessageDateMs: string;
  const lastMessageSent = messages.length > 0 ? messages[messages.length - 1] : null;
  const shouldMarkAsRead = hasUnreadMessage && !lastMessageSent?.isMessageOwner;

  // WebSocket integration
  const { subscribeToMessages, hadErrorSinceLastOpen, isWebSocketEnabledAndReady } = useWebSocketContext();

  const { updateLastPollTimestamp } = useSmartPolling({
    pollIntervalMs: aiConversationPollingInterval,
    refetch,
    startPolling,
    stopPolling,
    isEnabled: !!aiDisclaimer && !isWebSocketEnabledAndReady, // Enable for EliseAI conversations when WebSocket is not enabled and ready
  });

  const handleWebSocketMessage = useCallback(
    async (message: WebSocketMessage) => {
      if (message.event?.type === 'newMessage') {
        const messageConversationId = message.event.conversationId;

        if (conversationId === messageConversationId) {
          // Refetch message list only here. The effect below (on conversation length increase)
          // will refetch the conversation list once, avoiding duplicate getLatestConversations.
          if (WEBSOCKET_DEBUG) {
            logger.info('MessageList: Refetching due to new message in current conversation', {
              eventType: message.event.type,
              conversationId: messageConversationId,
            });
          }
          await refetch();
        }
      }
    },
    [conversationId, refetch],
  );

  useEffect(() => {
    if (!isWebSocketEnabledAndReady) return;
    return subscribeToMessages(handleWebSocketMessage, {
      conversationId: conversationId || undefined,
    });
  }, [isWebSocketEnabledAndReady, subscribeToMessages, handleWebSocketMessage, conversationId]);

  useEffect(() => {
    if (!isWebSocketEnabledAndReady || !hadErrorSinceLastOpen) return;
    refetch();
  }, [isWebSocketEnabledAndReady, hadErrorSinceLastOpen, refetch]);

  // Handle network status changes for polling
  useEffect(() => {
    if (networkStatus === 6) {
      updateLastPollTimestamp();
    }
  }, [networkStatus, updateLastPollTimestamp]);

  // Only show typing indicator for AI Conversations where the user IS the owner of the last message sent.
  // Hide indicator if the last message was sent more than 90 seconds ago
  useEffect(() => {
    if (!lastMessageSent || !lastMessageSent?.messageDateMs) {
      setShouldShowTypingIndicator(false);
      return;
    }

    const timeSinceLastMessageSent = new Date().getTime() - new Date(parseInt(lastMessageSent.messageDateMs)).getTime();
    const isLastMessageRecent = timeSinceLastMessageSent < TYPING_INDICATOR_TIMEOUT_MS;

    const baseShouldShowTypingIndicator =
      !!aiDisclaimer &&
      isLastMessageRecent &&
      !!lastMessageSent.isMessageOwner &&
      lastMessageSent.sendStatus !== MESSAGE_STATUS.FAILED &&
      lastMessageSent.sendStatus !== MESSAGE_STATUS.SENDING;

    setShouldShowTypingIndicator(baseShouldShowTypingIndicator);

    if (baseShouldShowTypingIndicator) {
      const timeoutId = setTimeout(() => {
        setShouldShowTypingIndicator(false);
      }, TYPING_INDICATOR_TIMEOUT_MS);
      return () => clearTimeout(timeoutId);
    }
  }, [aiDisclaimer, lastMessageSent]);

  useEffect(() => {
    /**
     * As the AddMessage text area grows in height, scroll MessageList
     * to the bottom so that the latest message is always in view
     */
    if (window.ResizeObserver && addMessageContainerRef.current) {
      resizeObserver = new ResizeObserver(() => {
        if (messageListContainerRef?.current) {
          messageListContainerRef.current.scrollTo(0, messageListContainerRef.current.scrollHeight);
        }
      });
      resizeObserver.observe(addMessageContainerRef.current);
    }

    // Disconnect on component unmount
    return () => {
      addMessageContainerRef?.current && resizeObserver?.unobserve(addMessageContainerRef.current);
      resizeObserver?.disconnect();
    };
  }, []);

  useEffect(() => {
    if (shouldShowChatDisabledMessage) {
      const notAcceptingRepliesMessageObject: DecoratedMessage = {
        attachments: [],
        messageType: MessageTypes.NotAcceptingReplies,
        messageDateMs: '',
        isMessageOwner: false,
        message: acceptsRepliesExplanation,
      };
      messages.push(notAcceptingRepliesMessageObject);
    }
  }, [messages]);

  useEffect(() => {
    if (!loading) {
      setShouldShowLoadingSkeleton(false);
    }
  }, [loading]);

  useEffect(() => {
    // Clear state on conversationId change
    setTemporaryMessages([]);
    setPreviousPaginatedMessageCount(null);
    setPreviousPaginatedMessages([]);
    setShouldShowLoadingSkeleton(loading && !messageListData);
  }, [conversationId]);

  // Handle scrolling and notifications for various scenarios
  useEffect(() => {
    if (shouldShowLoadingSkeleton) return;

    // Scenario 1: ConversationId changed - scroll to bottom
    if (conversationId) {
      return shouldMarkAsRead ? scrollToBottomAndMarkAsRead() : scrollToBottom();
    }
  }, [conversationId, shouldShowLoadingSkeleton]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (isLoadingMoreMessages || loading) return;

    // Scenario 2: New unread message and user is not at bottom - show notification
    if (!isMessagesEndInViewport && hasUnreadMessage) {
      setShouldShowNewMessageNotification(true);
      return;
    }

    // Scenario 3: New message added (sent or received) and user is at bottom - scroll to bottom
    if (isMessagesEndInViewport) {
      return shouldMarkAsRead ? scrollToBottomAndMarkAsRead() : scrollToBottom();
    }
  }, [messages, isLoadingMoreMessages]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    // Scenario 4: An optimistic message or typing indicator is displayed - scroll to bottom
    scrollToBottom();
  }, [temporaryMessages, shouldShowTypingIndicator]); // eslint-disable-line react-hooks/exhaustive-deps

  const prevMessageCountRef = useRef<number | null>(null);

  useEffect(() => {
    const conversation = RH_getConversationById?.conversation;
    const currentCount = conversation?.length;

    // If currentCount is undefined, skip
    if (typeof currentCount !== 'number') return;

    // On first data load, just set the ref and skip effect
    if (prevMessageCountRef.current === null) {
      prevMessageCountRef.current = currentCount;
      return;
    }

    if (currentCount > prevMessageCountRef.current) {
      // Only invalidate when the new message is from the other party (not us).
      // When we send a message, AddMessage already invalidates; skip here to avoid duplicate API calls.
      const lastMessage = conversation?.[currentCount - 1];
      const newMessageIsFromCurrentUser = lastMessage?.isMessageOwner === true;
      if (!newMessageIsFromCurrentUser) {
        // New message detected from other party (e.g. WebSocket or poll); refetch conversation list
        // so the sidebar shows unread dot, updated preview, and correct hasUnreadMessage.
        // Use refetchQueries to ensure the getLatestConversations request is made and UI updates.
        queryClient.refetchQueries({
          predicate: (query) =>
            query.queryKey[0] === SatelliteWebRenterConversationHandler_getLatestConversations_Path &&
            (query.queryKey[1] as { folder?: string })?.folder === mapFolderToApiType(folder),
        });
      }
    }

    prevMessageCountRef.current = currentCount;
  }, [RH_getConversationById?.conversation, folder, queryClient]);

  const handleLoadMoreMessages = async () => {
    try {
      setIsLoadingMoreMessages(true);

      await fetchMore({
        variables: {
          paginationStartIndex: messages[0].messageId,
        },
        updateQuery: (previousResult, { fetchMoreResult }) => {
          if (!fetchMoreResult) {
            return previousResult;
          }

          const newMessages = fetchMoreResult.RH_getConversationById.conversation;
          const updatedMessages = [...newMessages, ...messages];

          setPreviousPaginatedMessages(updatedMessages);
          setPreviousPaginatedMessageCount(newMessages.length);

          return {
            ...previousResult,
            RH_getConversationById: {
              ...previousResult.RH_getConversationById,
              conversation: updatedMessages,
            },
          };
        },
      });
    } catch (err) {
      logError({ error: err, context: 'Error fetching more messages' });
    }
  };

  function scrollToBottom() {
    if (shouldShowNewMessageNotification) {
      setShouldShowNewMessageNotification(false);
    }

    if (messageListEndRef.current) {
      messageListEndRef.current.scrollIntoView({
        behavior: 'smooth',
        block: 'end',
        inline: 'end',
      });
    }
  }

  function scrollToBottomAndMarkAsRead() {
    scrollToBottom();
    markConversationAsRead();
  }

  async function markConversationAsRead() {
    const lastReadMs = messages[messages.length - 1].messageDateMs;

    if (isImpersonator || !conversationId || !lastReadMs) return;

    try {
      await updateConversationStatusMutation(
        {
          postData: {
            conversationId,
            lastReadMs: Number(lastReadMs),
          },
        },
        {
          onSuccess: () => {
            // Update conversation list cache so sidebar unread dot clears without a second getLatestConversations call.
            queryClient.setQueriesData(
              {
                predicate: (query) =>
                  query.queryKey[0] === SatelliteWebRenterConversationHandler_getLatestConversations_Path &&
                  (query.queryKey[1] as { folder?: string })?.folder === mapFolderToApiType(folder),
              },
              (
                data:
                  | {
                      pages?: Array<{ conversations?: Array<{ conversationId?: string; hasUnreadMessage?: boolean }> }>;
                      pageParams?: Array<unknown>;
                    }
                  | undefined,
              ) => {
                if (!data?.pages) return data;
                const conversationIdStr = String(conversationId);
                return {
                  ...data,
                  pages: data.pages.map((page) => ({
                    ...page,
                    conversations: (page.conversations ?? []).map((conv) =>
                      String(conv.conversationId) === conversationIdStr ? { ...conv, hasUnreadMessage: false } : conv,
                    ),
                  })),
                };
              },
            );

            // Update the message list cache to mark this conversation as read (still using GraphQL/Apollo)
            const messageListQuery = {
              query: MessageList_Rh_GetConversationDocument,
              variables: {
                conversationId: conversationId || '',
              },
            };

            apolloClient.cache.updateQuery<MessageList_Rh_GetConversationQuery>(messageListQuery, (data) => {
              if (data?.RH_getConversationById) {
                return {
                  ...data,
                  RH_getConversationById: {
                    ...data.RH_getConversationById,
                    hasUnreadMessage: false,
                  },
                };
              }
              return data;
            });
          },
        },
      );
    } catch (error) {
      logError({ error, context: 'Error marking conversation as read' });
    }
  }

  const handleNotificationCloseClick = () => setShouldShowNewMessageNotification(false);

  const updateTemporaryMessage = (temporaryMessageKey: string, updates: Partial<DecoratedMessage>): void => {
    setTemporaryMessages((prevTemporaryMessages) => {
      const index = prevTemporaryMessages.findIndex(
        (temporaryMessage) => temporaryMessage.temporaryMessageKey === temporaryMessageKey,
      );

      if (index === -1) {
        return prevTemporaryMessages;
      }

      const updatedTemporaryMessages = [...prevTemporaryMessages];
      updatedTemporaryMessages[index] = {
        ...updatedTemporaryMessages[index],
        ...updates,
      };

      return updatedTemporaryMessages;
    });
  };

  const handleAddMessage = (optimisticMessage: DecoratedMessage) => {
    const lastTempMessage = temporaryMessages[temporaryMessages.length - 1];
    const isSameTemporaryKey = lastTempMessage?.temporaryMessageKey === optimisticMessage.temporaryMessageKey;

    if (lastTempMessage && isSameTemporaryKey) {
      const updatedMessageText = `${lastTempMessage.message}\n${optimisticMessage.message}`;
      const updatedTemporaryMessages = [...temporaryMessages];
      updatedTemporaryMessages[temporaryMessages.length - 1].message = updatedMessageText;

      setTemporaryMessages(updatedTemporaryMessages);
    } else {
      setTemporaryMessages([...temporaryMessages, optimisticMessage]);
    }
  };

  return (
    <Box css={messageListWrapperStyles}>
      {error && !messageListData && <NoConversationsContent errorCode={errorType} showErrorExperience={true} />}

      {shouldShowNewMessageNotification && (
        <NewMessageNotificationBanner
          onActionClick={scrollToBottomAndMarkAsRead}
          onClose={handleNotificationCloseClick}
        />
      )}

      {shouldShowReloginNotification && (
        <Banner.Root tone="critical">
          <Banner.Body>
            Your session has expired, please{' '}
            <Anchor href={signInPath} onImpact>
              login
            </Anchor>{' '}
            to restore your session.
          </Banner.Body>
        </Banner.Root>
      )}

      <Box css={messageListThreadStyles} ref={messageListContainerRef}>
        {shouldShowLoadingSkeleton ? (
          <MessageListContentsSkeleton />
        ) : (
          <>
            <Box
              css={{
                display: 'flex',
                justifyContent: 'center',
                flexDirection: 'column',
                marginBlock: 'default',
              }}
            >
              {shouldShowStartConversationText ? (
                <Box css={conversationIntroTextStyles}>
                  <Paragraph textStyle="body-sm-bold" css={{ marginBottom: 'tighter' }}>
                    {StringConstants.START_CONVERSATION_TEXT}
                  </Paragraph>

                  <Paragraph textStyle="body-xs" css={{ textAlign: 'center', maxWidth: '700px' }}>
                    {StringConstants.WARNING_MESSAGE.text}{' '}
                    <Anchor href={StringConstants.WARNING_MESSAGE.url} target="_blank">
                      Read this article
                    </Anchor>{' '}
                    for more tips on avoiding scams.
                  </Paragraph>

                  {aiDisclaimer && (
                    <Disclaimer
                      disclaimerText={aiDisclaimer.disclaimerText}
                      links={aiDisclaimer.links}
                      emailLinks={aiDisclaimer.emailLinks}
                    />
                  )}
                </Box>
              ) : (
                <TextButton onClick={handleLoadMoreMessages} disabled={isLoadingMoreMessages}>
                  {isLoadingMoreMessages ? 'Loading more messages...' : 'Load more messages'}
                </TextButton>
              )}
            </Box>

            <List.Root appearance="none" data-testid="message-list" css={{ marginBottom: 'loose' }}>
              {messages.map((conversationMessage, idx) => {
                const isLastMessageInList = idx === messages.length - 1;
                const { attachments, message, messageDateMs, messageId, sendStatus } = conversationMessage;
                const messageIsSameDayAsPrevious = prevMessageDateMs && isSameDay(+messageDateMs, +prevMessageDateMs);
                const timestamp =
                  messageIsSameDayAsPrevious || !messageDateMs ? null : getMessageTimestamp(messageDateMs);
                prevMessageDateMs = messageDateMs;

                // Edge case: message objects with missing message AND attachments fields - return "failed to load" item
                if (!message && !attachments) {
                  const failedToLoadMessage = {
                    ...conversationMessage,
                    message: MESSAGE_LOAD_ERROR_TEXT,
                    messageType: MESSAGE_TYPES.SYSTEM_MESSAGE.FAILED_TO_LOAD,
                    uiTreatmentType: MESSAGE_TYPES.LOW_PRIORITY,
                  };

                  return (
                    <MessageItem
                      key={messageId}
                      brand={brand}
                      conversationId={conversationId || ''}
                      isLastMessageInList={isLastMessageInList}
                      message={failedToLoadMessage}
                      messageKey={messageId!}
                      selectedFolder={folder}
                      sendStatus={sendStatus}
                      shouldFetchOlderConversations={shouldFetchOlderConversations}
                      updateTemporaryMessage={updateTemporaryMessage}
                    />
                  );
                }

                // Edge case: message field with no text AND no attachments - do not render
                if (
                  stringUtils.isEmptyString(conversationMessage.message || '') &&
                  conversationMessage.attachments.length === 0
                ) {
                  return null;
                }

                return (
                  <React.Fragment key={messageId}>
                    {timestamp && (
                      <Text
                        textStyle="body-xs"
                        css={{
                          display: 'block',
                          color: 'text.subtle',
                          marginBlock: 'default',
                          textAlign: 'center',
                        }}
                        asChild
                      >
                        <time>{timestamp}</time>
                      </Text>
                    )}

                    <MessageItem
                      brand={brand}
                      conversationId={conversationId || ''}
                      isLastMessageInList={isLastMessageInList}
                      message={conversationMessage}
                      messageKey={messageId!}
                      selectedFolder={folder}
                      sendStatus={sendStatus}
                      shouldFetchOlderConversations={shouldFetchOlderConversations}
                      updateTemporaryMessage={updateTemporaryMessage}
                    />
                  </React.Fragment>
                );
              })}
            </List.Root>

            {shouldShowTypingIndicator && <TypingIndicator isAiEnabledConversation={!!aiDisclaimer} />}

            <div ref={messageListEndRef} />
          </>
        )}
      </Box>

      {acceptsReplies && (
        <AddMessage
          ref={addMessageContainerRef}
          handleAddMessage={handleAddMessage}
          isConversationBlockedByZillow={isConversationBlockedByZillow || false}
          shouldAllowSendingAttachmentsOnly={shouldAllowSendingAttachmentsOnly}
          shouldOpenAttachmentsUploader={shouldOpenAttachmentsUploader}
          shouldFetchOlderConversations={shouldFetchOlderConversations}
          updateTemporaryMessage={updateTemporaryMessage}
        />
      )}
    </Box>
  );
}
